import { CircularProgress } from "@mui/material";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { FeConfigType } from "config/config";
import ActionToolbar, {
    ActionToolbarProps,
} from "containers/Resources/components/CommonActions/ActionToolbar";
import { ListActionProgress } from "containers/Resources/components/CommonActions/ListActionProgress";
import { ListActionScopeContext } from "containers/Resources/listActionScope";
import { Record } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { MUIDataTableOptions, SelectableRows } from "mui-datatables";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./RecordTable.scss";
import StickyHorizontalScrollbar from "./StickyHorizontalScrollbar";
import Table from "./Table";
import WindowedTableBody from "./WindowedTableBody";
import TableColumnsSelector from "./TableColumnsSelector";
import TableDensitySelector from "./TableDensitySelector";
import { buildForest, collectAllIds, flattenForest } from "./treeRows";
import {
    buildGroupedRows,
    groupTreeAccessors,
    GroupWithMembers,
    isGroupHeaderRow,
} from "./groupRows";
import {
    TableColumn,
    TableDensity,
    DEFAULT_TABLE_DENSITY,
    TableTreeRenderConfig,
    convertLegacyColumnToMuiColumn,
    getInitialColumnWidths,
    getInitialDensity,
    getInitialTableColumns,
    getSortOrder,
    getTableSettings,
    readSessionSegmentPage,
    updateLocalStorageTableSettings,
    writeSessionSegmentPage,
} from "./tableUtils";

interface RecordTableProps<T> {
    columns: TableColumn[];
    data: T[];
    totalItemCount: number;
    isLoading: boolean;
    recordTypes: FeConfigType | "all" | null;
    updateQuery?: (change: any) => void;
    refresh?: () => void;
    /**
     * Called with the keys of the currently-visible (shown) columns whenever that
     * set changes (initial mount + each column-picker toggle). Opt-in: advanced
     * search uses it to fetch generic schema-driven columns only when displayed
     * (Option B). No effect for callers that don't pass it.
     * (operator-task-fe-advanced-search-file-content-type-column)
     */
    onVisibleColumnsChange?: (visibleColumnKeys: string[]) => void;
    options?: {
        selectableRows?: SelectableRows;
        /**
         * Toolbar mode:
         * - `undefined` / `"custom"` -> the full RecordTable toolbar (left
         *   `ActionToolbar` with bulk row actions + right refresh/density/columns
         *   controls).
         * - `"controls"` -> same right-side controls (refresh/density/columns)
         *   but WITHOUT the left `ActionToolbar` — for compact related-record
         *   panels (e.g. UsedByList, RecordLog) that use `selectableRows: "none"`
         *   and want column-selection without the stray bulk-action chrome.
         * - `"default"` -> mui-datatables' own default toolbar.
         * - `"none"` -> no toolbar at all.
         */
        toolbar?: "default" | "custom" | "controls" | "none";
        toolbarFilters?: boolean;
        customRightToolbarChildren?: React.ReactNode;
        /**
         * Extra nodes rendered on the LEFT of the toolbar, before the action
         * buttons (Move, …). Use for a container-level create/upload action that
         * belongs with the row-action buttons rather than the table-display
         * controls on the right. Unlike `customLeftToolbar` (which REPLACES the
         * default ActionToolbar), this only prepends to it.
         */
        customLeftToolbarChildren?: Node | Node[];
        customLeftToolbar?: (props: ActionToolbarProps<T>) => Node | Node[];
        density?: TableDensity;
        /**
         * Show the per-table row-density toggle in the toolbar (default true).
         * The chosen density persists per table under `tableSettingsKey`,
         * overriding the caller-provided `density` default. Set false to hide
         * the control (e.g. tables that must keep a fixed density).
         */
        showDensityToggle?: boolean;
        /**
         * Allow the user to drag column borders to resize columns; the chosen
         * pixel widths persist per table under `tableSettingsKey` and override
         * the type-aware default width caps. Double-clicking a grip clears the
         * override. Defaults to true. Set false for tables that must keep fixed
         * column sizing.
         */
        resizableColumns?: boolean;
        setRowProps?: (row: any[], dataIndex: number, rowIndex: number) => object;
        jumpToPage?: boolean;
        showSearch?: boolean;
        currentOffset?: number;
        isSearchAlwaysOpen?: boolean;
        viewColumns?: boolean;
        customFooter?: () => React.ReactNode;
        canSearchEverywhere?: boolean;
        skipFetchOnAction?: boolean;
        /**
         * Show a manual "Refresh" button in the toolbar for non-live views (e.g.
         * advanced search, resource lists) that fetch once and would otherwise go
         * stale until navigating away. No-op unless a `refresh` callback is also
         * passed. Live views (dashboard widgets with their own live-bar) should
         * NOT set this — they already have a refresh affordance.
         */
        showRefreshButton?: boolean;
        rowsPerPageOptions?: number[];
        tableBodyMaxHeight?: string;
        /**
         * Fix the table BODY to an exact height (mui-datatables `tableBodyHeight`),
         * independent of how many rows the current page holds. Use this when a
         * caller wants the footer to stay in a stable position across pages — e.g.
         * the AccessList popover reserves one full page of height so paging to a
         * shorter last page does not shrink the popover and move the pagination
         * control. Leave unset to let the body size to its content.
         */
        tableBodyHeight?: string;
        /**
         * Hide the pagination footer and render all rows (mui-datatables
         * `pagination`). Used by height-adaptive callers (e.g. the dashboard
         * record_list widget) that size the fetched row count to the available
         * height and therefore want a single, footer-less page. Defaults to true.
         */
        pagination?: boolean;
        isRowSelectable?: (dataIndex: number) => boolean;
        /**
         * Show a horizontal scrollbar pinned to the bottom of the scroll
         * viewport ("sticky-to-viewport-bottom") for wide tables that overflow
         * horizontally, so the scrollbar is discoverable and reachable without
         * scrolling to the very bottom of a long list. Only has an effect when
         * the table's own horizontal scroll container (mui-datatables
         * `responsiveBase`) actually scrolls — i.e. contexts that keep
         * `overflow-x: auto` on it (e.g. the object_template CO / subrecord
         * lists). Defaults to false. See operator-task-fe-wide-list-top-or-
         * sticky-horizontal-scrollbar.
         */
        stickyHorizontalScrollbar?: boolean;
        /** Remember current page for client-side tables (no updateQuery) in sessionStorage. */
        clientSidePaginationPersist?: {
            storageKey: string;
            segmentKey?: string;
        };
        /**
         * Bound the mounted `<tr>` count by spacer-row virtualization: only the
         * rows near the viewport are mounted, off-viewport rows are replaced by
         * two height-reserving spacer rows (see {@link WindowedTableBody}). For
         * un-paginated, potentially-huge single lists — the Logs tab's
         * accumulating gradual-log list — so a high-volume record can't grow an
         * unbounded DOM. Requires uniform fixed-height rows (always true via the
         * `.recordTableCellClip` clamp). Off by default; every other list keeps
         * the stock body. See operator-task-fe-logs-tab-bounded-dom-virtualization.
         */
        windowedBody?: boolean;
        /**
         * Opt-in parent/child TREE mode. When present, RecordTable assembles the
         * flat `data` into a forest (via each row's `getParentId`) and renders it
         * as a depth-annotated, expandable/collapsible list: the `indentColumnKey`
         * column (default `name`) is indented by depth and carries an
         * expand/collapse caret + a descendant-count badge; every other column
         * reuses its normal cell renderer. The expand state is held INTERNALLY as a
         * COLLAPSED set (everything expanded by default, so late-arriving live rows
         * show expanded rather than silently hidden); expand-all / collapse-all live
         * in the toolbar. Header-click column sort is disabled in tree mode so
         * parent/child contiguity is preserved, and the list renders unpaginated
         * (the caller fetches a bounded batch). Tree OFF ⇒ byte-identical rendering
         * (same opt-in discipline as `windowedBody` virtualization) — the ~20 other
         * RecordTable consumers are unaffected.
         * (operator-task-fe-recordtable-tree-mode-phase1-children-tab)
         */
        tree?: {
            getRowId: (row: any) => string;
            getParentId: (row: any) => string | null | undefined;
            indentColumnKey?: string;
            indentPx?: number;
        };
        /**
         * Opt-in row GROUPING. Rows are partitioned by `getGroupKey` and rendered
         * under one header row per group (first-seen order, or `sortGroups`). The
         * header shows the group label in the `indentColumnKey` column (default
         * `name`) with an expand/collapse caret and a member-count badge; its other
         * cells stay empty and it is not selectable. Built on tree mode, so it has
         * the same constraints: client-side, unpaginated, no header-click sort, and
         * expand-all / collapse-all in the toolbar. Ignored when `tree` is set.
         * Grouping OFF ⇒ byte-identical rendering.
         * (operator-task-fe-recordtable-row-grouping-support-and-latestn)
         */
        groupBy?: {
            getGroupKey: (row: any) => string;
            getGroupLabel?: (row: any, key: string) => string;
            sortGroups?: (groups: GroupWithMembers<any>[]) => GroupWithMembers<any>[];
            /** Row id accessor (default `row.id`). */
            getRowId?: (row: any) => string;
            indentColumnKey?: string;
            indentPx?: number;
        };
        /**
         * Expandable-row support, threaded straight to mui-datatables. Off by
         * default; only the record-metadata editor (MetadataList) uses it today.
         * `renderExpandableRow(rowData, rowMeta)` renders the expanded content for
         * a row (rowMeta.dataIndex -> the data row). `rowsExpanded` +
         * `onRowExpansionChange` make the expanded set CONTROLLED, which a consumer
         * needs because mui-datatables UNMOUNTS a collapsed expanded row — so the
         * consumer can keep a row open (e.g. while its inline form is dirty) and
         * not lose that row's state. `expandableRowsHeader` shows the select-all
         * expand toggle in the header (default off here);
         * `expandableRowsOnClick` expands on whole-row click (default off, so an
         * inline control in a collapsed cell stays clickable).
         * (operator-task-fe-convert-independent-renderers-to-recordtable)
         */
        expandableRows?: boolean;
        renderExpandableRow?: (
            rowData: any[],
            rowMeta: { dataIndex: number; rowIndex: number }
        ) => React.ReactNode;
        rowsExpanded?: any[];
        onRowExpansionChange?: (
            currentRowsExpanded: any[],
            allRowsExpanded: any[],
            rowsExpanded?: any[]
        ) => void;
        expandableRowsHeader?: boolean;
        expandableRowsOnClick?: boolean;
        /**
         * `isRowExpandable(dataIndex, expandedRows) => boolean` — threaded to
         * mui-datatables, which consults it on every expand/collapse toggle and
         * HIDES a row's chevron when it returns false. A consumer uses it to
         * BLOCK collapsing a row whose inline form is dirty: controlling
         * `rowsExpanded` alone cannot prevent the collapse, because mui-datatables
         * unmounts (and so resets) the row's form on the collapse click before the
         * controlled prop re-asserts it. Returning false here stops that toggle at
         * the source, keeping the dirty row mounted and its unsaved edit intact.
         */
        isRowExpandable?: (dataIndex: number, expandedRows?: any) => boolean;
    };
    passSelectedItemsToParent?: (selectedItems: any[]) => void;
    shouldResetSelection?: boolean;
    initialQuery?: { limit: number; offset?: number; order: string };
    tableSettingsKey?: string;
    onSearchChange?: (searchText: string | null) => void;
    searchPlaceholder?: string;
    className?: string;
    isActionButtonDisabled?: boolean;
    actionButtonLabel?: string;
    filterParams?: {
        isTopLevel?: boolean;
        deletedOnly?: boolean;
        normalOnly?: boolean;
    };
    isContainerGitEnabled?: boolean;
    isBundle?: boolean;
    isDisabled?: boolean;
}

/**
 * Data consumed by {@link RecordTableToolbar}. Provided via context so the
 * toolbar keeps a stable component identity — mui-datatables remounts
 * `components.TableToolbar` whenever the component *reference* changes, which
 * would snap shut an open columns popover and reset toolbar-local state on
 * every spinner toggle or row (de)selection.
 */
interface RecordTableToolbarContextValue {
    formattedSelectedItems: { resourceName: any; resourceData: any }[];
    customLeftToolbar?: (props: ActionToolbarProps<any>) => Node | Node[];
    recordTypes: FeConfigType | "all" | null;
    afterButtonAction: () => void;
    isActionButtonDisabled?: boolean;
    actionButtonLabel?: string;
    filterParams?: RecordTableProps<any>["filterParams"];
    isContainerGitEnabled?: boolean;
    isBundle?: boolean;
    isLoading: boolean;
    customRightToolbarChildren?: React.ReactNode;
    customLeftToolbarChildren?: Node | Node[];
    viewColumns?: boolean;
    /**
     * Hide the left-hand list/bulk-action chrome (`ActionToolbar` /
     * `customLeftToolbar` / `customLeftToolbarChildren`), keeping only the
     * right-side display controls. Set by the `"controls"` toolbar mode.
     */
    hideListActions?: boolean;
    /** Whether to render the manual refresh button (already AND-ed with `refresh` presence). */
    showRefreshButton: boolean;
    setCols: React.Dispatch<React.SetStateAction<TableColumn[]>>;
    tableSettingsKey: string;
    columns: TableColumn[];
    density: TableDensity;
    setDensity: (density: TableDensity) => void;
    showDensityToggle: boolean;
    /**
     * Present only in tree mode (`options.tree`): expand-all / collapse-all the
     * whole forest. Rendered on the left of the toolbar regardless of the
     * `hideListActions` ("controls") mode.
     */
    treeControls?: { expandAll: () => void; collapseAll: () => void };
}

const RecordTableToolbarContext = createContext<RecordTableToolbarContextValue | null>(null);

/**
 * Stable toolbar component. Its identity never changes, so mui-datatables never
 * remounts it; dynamic data flows in through {@link RecordTableToolbarContext},
 * so context changes re-render (rather than remount) the toolbar.
 */
function RecordTableToolbar() {
    const ctx = useContext(RecordTableToolbarContext);
    if (!ctx) return null;
    const {
        formattedSelectedItems,
        customLeftToolbar,
        recordTypes,
        afterButtonAction,
        isActionButtonDisabled,
        actionButtonLabel,
        filterParams,
        isContainerGitEnabled,
        isBundle,
        isLoading,
        customRightToolbarChildren,
        customLeftToolbarChildren,
        viewColumns,
        hideListActions,
        showRefreshButton,
        setCols,
        tableSettingsKey,
        columns,
        density,
        setDensity,
        showDensityToggle,
        treeControls,
    } = ctx;

    return (
        <div className="toolbar">
            <div className="left">
                {treeControls && (
                    <span className="recordTableTreeControls">
                        <Button
                            variation="link"
                            size="small"
                            icon={IconCollection.arrowExpand}
                            label="Expand all"
                            title="Expand all"
                            onClick={treeControls.expandAll}
                        />
                        <Button
                            variation="link"
                            size="small"
                            icon={IconCollection.arrowCollapse}
                            label="Collapse all"
                            title="Collapse all"
                            onClick={treeControls.collapseAll}
                        />
                    </span>
                )}
                {!hideListActions && (
                    <ListActionScopeContext.Provider value={tableSettingsKey}>
                        <ListActionProgress />
                        {customLeftToolbarChildren && (
                            <span className="customLeftToolbarChildren">
                                {customLeftToolbarChildren}
                            </span>
                        )}
                        {customLeftToolbar ? (
                            customLeftToolbar({
                                selectedItems: formattedSelectedItems,
                                recordTypes,
                                afterButtonAction,
                                isDisabled: isActionButtonDisabled,
                                actionButtonLabel,
                                filterParams:
                                    tableSettingsKey === "execution_filter"
                                        ? filterParams
                                        : undefined,
                            })
                        ) : (
                            <ActionToolbar
                                selectedItems={formattedSelectedItems}
                                recordTypes={recordTypes}
                                afterButtonAction={afterButtonAction}
                                isContainerGitEnabled={isContainerGitEnabled}
                                isBundle={isBundle}
                            />
                        )}
                    </ListActionScopeContext.Provider>
                )}
            </div>
            <div className="spacer">
                <CircularProgress
                    disableShrink
                    className={`spinner ${isLoading ? "" : "hidden"}`}
                />
            </div>
            <div className="right">
                {customRightToolbarChildren}
                {showRefreshButton && (
                    <Button
                        variation="clear"
                        label="Refresh"
                        title="Refresh"
                        icon={IconCollection.refresh}
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={afterButtonAction}
                    />
                )}
                {showDensityToggle && (
                    <TableDensitySelector density={density} setDensity={setDensity} />
                )}
                {viewColumns !== false && (
                    <TableColumnsSelector
                        setTableColumns={setCols}
                        tableSettingsKey={tableSettingsKey}
                        initialColumns={columns}
                    />
                )}
            </div>
        </div>
    );
}

/**
 * A comprehensive data table component built on top of MUI DataTable
 * with support for server-side operations, row selection, custom toolbars,
 * and persistent table settings.
 *
 * @template T - The type of data records, must extend Record with id and optional name
 * @param props - RecordTableProps object containing all configuration
 * @returns JSX.Element - The rendered table component
 *
 */
function RecordTable<T extends Pick<Record, "id"> & { name?: string }>({
    columns,
    data,
    totalItemCount,
    isLoading,
    shouldResetSelection,
    recordTypes,
    updateQuery,
    refresh,
    onVisibleColumnsChange,
    options,
    tableSettingsKey = "",
    initialQuery,
    onSearchChange,
    searchPlaceholder,
    className,
    isActionButtonDisabled,
    actionButtonLabel,
    passSelectedItemsToParent,
    isContainerGitEnabled,
    isBundle,
    isDisabled,
    filterParams,
}: RecordTableProps<T>) {
    const initialSelection: Array<any> = [];
    const [selectedItems, setSelectedItems] = useState(initialSelection);
    const [selectedIndexes, setSelectedIndexes] = useState(initialSelection);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialQuery?.limit || 10);
    const [cols, setCols] = useState<TableColumn[]>([]);

    useEffect(
        () => setCols(getInitialTableColumns(columns, tableSettingsKey)),
        [columns, tableSettingsKey]
    );

    // ---- Opt-in parent/child tree mode (options.tree) ----------------------
    // Assemble the flat `data` into a forest and flatten it back into a
    // depth-annotated display order. Track a COLLAPSED set (default: everything
    // expanded) rather than an expanded set, so a node that arrives later via a
    // live subscription shows expanded by default instead of silently hidden.
    // Tree OFF ⇒ everything below is null and `displayData === data`, so the
    // non-tree render path is byte-identical.
    // Grouping (options.groupBy) is tree mode over synthetic group header rows:
    // each data row becomes a child of its group's header. `treeSourceData` is
    // the flat input of the forest build (data + interleaved headers).
    const groupCfg = options?.tree ? undefined : options?.groupBy;
    const groupTreeCfg = useMemo(() => {
        if (!groupCfg) return undefined;
        const accessors = groupTreeAccessors(
            groupCfg.getRowId ?? ((row: any) => String(row?.id)),
            groupCfg.getGroupKey
        );
        return {
            ...accessors,
            indentColumnKey: groupCfg.indentColumnKey,
            indentPx: groupCfg.indentPx ?? 0,
        };
    }, [groupCfg]);
    const treeSourceData = useMemo(
        () =>
            groupCfg
                ? buildGroupedRows(data, {
                      getGroupKey: groupCfg.getGroupKey,
                      getGroupLabel: groupCfg.getGroupLabel,
                      sortGroups: groupCfg.sortGroups,
                  })
                : data,
        [groupCfg, data]
    );
    const treeCfg = options?.tree ?? groupTreeCfg;
    const [treeCollapsedIds, setTreeCollapsedIds] = useState<Set<string>>(() => new Set());
    const treeForest = useMemo(
        () =>
            treeCfg
                ? buildForest(treeSourceData, {
                      getRowId: treeCfg.getRowId,
                      getParentId: treeCfg.getParentId,
                  })
                : null,
        [treeCfg, treeSourceData]
    );
    const treeAllIds = useMemo(
        () => (treeForest ? collectAllIds(treeForest) : null),
        [treeForest]
    );
    const treeExpandedIds = useMemo(() => {
        if (!treeAllIds) return null;
        const expanded = new Set<string>();
        treeAllIds.forEach((id) => {
            if (!treeCollapsedIds.has(id)) expanded.add(id);
        });
        return expanded;
    }, [treeAllIds, treeCollapsedIds]);
    const treeFlat = useMemo(
        () =>
            treeForest && treeExpandedIds
                ? flattenForest(treeForest, treeExpandedIds)
                : null,
        [treeForest, treeExpandedIds]
    );
    // Rows actually rendered: the flattened forest in tree mode, else `data`
    // untouched (same reference ⇒ byte-identical non-tree path).
    const displayData = useMemo(
        () => (treeFlat ? treeFlat.map((flatRow) => flatRow.row) : data),
        [treeFlat, data]
    );
    const toggleTreeNode = useCallback((id: string) => {
        setTreeCollapsedIds((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);
    const expandAllTree = useCallback(() => setTreeCollapsedIds(new Set()), []);
    const collapseAllTree = useCallback(
        () => setTreeCollapsedIds(new Set(treeAllIds ?? [])),
        [treeAllIds]
    );
    const treeRenderConfig = useMemo<TableTreeRenderConfig | undefined>(() => {
        if (!treeCfg || !treeFlat || !treeExpandedIds) return undefined;
        return {
            indentColumnKey: treeCfg.indentColumnKey ?? "name",
            indentPx: treeCfg.indentPx ?? 20,
            rows: treeFlat,
            isExpanded: (id: string) => treeExpandedIds.has(id),
            onToggle: toggleTreeNode,
        };
    }, [treeCfg, treeFlat, treeExpandedIds, toggleTreeNode]);

    // Report the visible-column set upward when it changes (mount + each picker
    // toggle), so an opt-in caller can fetch data only for the columns actually
    // displayed (Option B). The signature guard prevents re-firing (and any
    // fetch loop) when a re-render leaves the visible set unchanged.
    // (operator-task-fe-advanced-search-file-content-type-column)
    const lastVisibleKeysRef = useRef<string>(" uninitialized");
    useEffect(() => {
        if (!onVisibleColumnsChange) return;
        const visibleKeys = cols.filter((col) => col.shown).map((col) => col.key);
        const signature = visibleKeys.join(" ");
        if (signature === lastVisibleKeysRef.current) return;
        lastVisibleKeysRef.current = signature;
        onVisibleColumnsChange(visibleKeys);
    }, [cols, onVisibleColumnsChange]);

    // Wrapper element for the optional sticky-to-viewport-bottom horizontal
    // scrollbar to resolve the mui-datatables responsiveBase scroll node beneath.
    const recordTableWrapperRef = useRef<HTMLDivElement>(null);

    // Per-table row density: the persisted user choice (settings key) overrides
    // the caller-provided `options.density` default; falls back to the shared
    // DEFAULT_TABLE_DENSITY so every list screen has the same row height.
    const densityDefault = options?.density ?? DEFAULT_TABLE_DENSITY;
    const [density, setDensity] = useState<TableDensity>(() =>
        getInitialDensity(tableSettingsKey, densityDefault)
    );
    useEffect(() => {
        setDensity(getInitialDensity(tableSettingsKey, densityDefault));
    }, [tableSettingsKey, densityDefault]);
    const applyDensity = useCallback(
        (newDensity: TableDensity) => {
            setDensity(newDensity);
            updateLocalStorageTableSettings(tableSettingsKey, { density: newDensity });
        },
        [tableSettingsKey]
    );

    // Per-column drag-to-resize widths (px), keyed by column key. Persisted per
    // table under the settings key, overriding the type-aware default caps.
    // During a drag `applyColumnWidth` is called with commit=false on every
    // mouse-move (state-only, live preview) and commit=true once on release
    // (state + persist), so we don't thrash localStorage mid-drag.
    const resizableColumns = options?.resizableColumns ?? true;
    // NB: the global `Record` utility is shadowed in this file by the imported
    // Cloudomation `Record` GraphQL type, so use an index signature here.
    const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(() =>
        getInitialColumnWidths(tableSettingsKey)
    );
    // Mirror the latest widths in a ref so the stable callbacks below can build
    // the next map without capturing a stale `columnWidths` (they intentionally
    // depend only on `tableSettingsKey`) and without side-effecting inside a
    // setState updater.
    const columnWidthsRef = useRef(columnWidths);
    columnWidthsRef.current = columnWidths;
    useEffect(() => {
        setColumnWidths(getInitialColumnWidths(tableSettingsKey));
    }, [tableSettingsKey]);
    const applyColumnWidth = useCallback(
        (columnKey: string, width: number, commit: boolean) => {
            const next = { ...columnWidthsRef.current, [columnKey]: width };
            columnWidthsRef.current = next;
            setColumnWidths(next);
            // Persist only on release (commit) so a drag doesn't thrash storage.
            if (commit) {
                updateLocalStorageTableSettings(tableSettingsKey, { widths: next });
            }
        },
        [tableSettingsKey]
    );
    const resetColumnWidth = useCallback(
        (columnKey: string) => {
            if (!(columnKey in columnWidthsRef.current)) return;
            const next = { ...columnWidthsRef.current };
            delete next[columnKey];
            columnWidthsRef.current = next;
            setColumnWidths(next);
            updateLocalStorageTableSettings(tableSettingsKey, { widths: next });
        },
        [tableSettingsKey]
    );

    // Prefer a persisted sort (column + direction) stored under this table's
    // settings key over the caller's default `initialQuery.order`, so a user's
    // chosen sort survives a reload. The write side already exists — any table
    // with a `tableSettingsKey` writes its sort to the `order` setting in
    // onColumnSortChange (including client-side tables like the dashboard
    // record_list widget, which otherwise pass a hardcoded default order) — this
    // is the matching restore. For server-side callers the persisted order is
    // already folded into `initialQuery.order` (getInitialQueryWithTableSettings),
    // so preferring it here is idempotent. A persisted order whose column is gone
    // (or no longer sortable) in the current column set is ignored, falling back
    // to `initialQuery.order`.
    const persistedOrder = useMemo(() => {
        if (!tableSettingsKey) return null;
        const stored = getTableSettings(tableSettingsKey, "order");
        if (typeof stored !== "string" || stored.length < 2) return null;
        const sign = stored.slice(0, 1);
        if (sign !== "+" && sign !== "-") return null;
        const col = columns.find((c) => c.key === stored.slice(1));
        if (!col || col.sortable === false) return null;
        return stored;
    }, [tableSettingsKey, columns]);

    const effectiveInitialOrder = persistedOrder ?? initialQuery?.order;

    const initialSortOrder = useMemo(
        () =>
            effectiveInitialOrder
                ? {
                      name: getSortOrder(effectiveInitialOrder).name,
                      direction: getSortOrder(effectiveInitialOrder).direction,
                  }
                : null,
        [effectiveInitialOrder]
    );
    const [sortOrder, setSortOrder] = useState(initialSortOrder);

    useEffect(() => {
        setSortOrder(initialSortOrder);
    }, [initialSortOrder, recordTypes]);

    useEffect(() => {
        setRowsPerPage(initialQuery?.limit || 10);
    }, [initialQuery?.limit, recordTypes]);

    const clientPageCfg = !updateQuery ? options?.clientSidePaginationPersist : undefined;
    const clientPagePersist = Boolean(clientPageCfg?.storageKey);
    const clientPageStorageKey = clientPageCfg?.storageKey ?? "";
    const clientPageSegmentKey = clientPageCfg?.segmentKey ?? "_";

    const [clientTablePage, setClientTablePage] = useState(() => {
        if (!clientPageCfg?.storageKey) return 0;
        return readSessionSegmentPage(
            clientPageCfg.storageKey,
            clientPageCfg.segmentKey ?? "_"
        );
    });

    useEffect(() => {
        if (!clientPagePersist) {
            setClientTablePage(0);
            return;
        }
        setClientTablePage(
            readSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey)
        );
    }, [clientPagePersist, clientPageStorageKey, clientPageSegmentKey]);

    useEffect(() => {
        if (!clientPagePersist) return;
        const maxPageIndex = Math.max(0, Math.ceil((totalItemCount || 0) / rowsPerPage) - 1);
        setClientTablePage((prev) => {
            if (prev <= maxPageIndex) return prev;
            writeSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey, maxPageIndex);
            return maxPageIndex;
        });
    }, [
        clientPagePersist,
        clientPageStorageKey,
        clientPageSegmentKey,
        totalItemCount,
        rowsPerPage,
    ]);

    const maxPageIndexForClient = Math.max(
        0,
        Math.ceil((totalItemCount || 0) / rowsPerPage) - 1
    );
    const clientTablePageForMui = clientPagePersist
        ? Math.min(clientTablePage, maxPageIndexForClient)
        : undefined;

    // Sync selected items with current data to handle data updates
    useEffect(() => {
        // eslint-disable-next-line eqeqeq
        if (selectedItems.length && displayData != undefined) {
            const selectedData: { selectedIndexes: number[]; selectedItems: T[] } = {
                selectedIndexes: [],
                selectedItems: [],
            };

            // Find current indices of selected items in the (display-order) data
            selectedItems.forEach((selectedItem) => {
                const selectedIndex = displayData.findIndex((item) => {
                    return item.id ? item.id === selectedItem.id : item.name === selectedItem.name;
                });

                if (selectedIndex > -1) {
                    selectedData.selectedIndexes.push(selectedIndex);
                    selectedData.selectedItems.push(selectedItem);
                }
            });

            setSelectedIndexes(selectedData.selectedIndexes);
            // Only update selected items if they've actually changed
            if (!isEqual(selectedItems, selectedData.selectedItems)) {
                setSelectedItems(selectedData.selectedItems);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayData]);

    const resetSelectionAndRefresh = useCallback(() => {
        setSelectedIndexes([]);
        setSelectedItems([]);
        if (refresh && !options?.skipFetchOnAction) refresh();
    }, [options?.skipFetchOnAction, refresh]);

    useEffect(() => {
        if (shouldResetSelection) {
            resetSelectionAndRefresh();
        }
    }, [shouldResetSelection, resetSelectionAndRefresh]);

    // Notify parent component of selected items changes
    useEffect(
        () => passSelectedItemsToParent?.(selectedItems),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedItems, passSelectedItemsToParent]
    );

    const toolbarContextValue = useMemo<RecordTableToolbarContextValue>(
        () => ({
            formattedSelectedItems: selectedItems.map((x) => ({
                resourceName: x.resource_type || x.activity_type,
                resourceData: x,
            })),
            customLeftToolbar: options?.customLeftToolbar,
            recordTypes,
            afterButtonAction: resetSelectionAndRefresh,
            isActionButtonDisabled,
            actionButtonLabel,
            filterParams,
            isContainerGitEnabled,
            isBundle,
            isLoading,
            customRightToolbarChildren: options?.customRightToolbarChildren,
            customLeftToolbarChildren: options?.customLeftToolbarChildren,
            viewColumns: options?.viewColumns,
            hideListActions: options?.toolbar === "controls",
            showRefreshButton: Boolean(options?.showRefreshButton && refresh),
            setCols,
            tableSettingsKey,
            columns,
            density,
            setDensity: applyDensity,
            showDensityToggle: options?.showDensityToggle ?? true,
            treeControls: treeRenderConfig
                ? { expandAll: expandAllTree, collapseAll: collapseAllTree }
                : undefined,
        }),
        [
            selectedItems,
            density,
            applyDensity,
            treeRenderConfig,
            expandAllTree,
            collapseAllTree,
            options?.showDensityToggle,
            options?.customLeftToolbar,
            options?.customRightToolbarChildren,
            options?.customLeftToolbarChildren,
            options?.viewColumns,
            options?.toolbar,
            options?.showRefreshButton,
            refresh,
            recordTypes,
            resetSelectionAndRefresh,
            isActionButtonDisabled,
            actionButtonLabel,
            filterParams,
            isContainerGitEnabled,
            isBundle,
            isLoading,
            tableSettingsKey,
            columns,
        ]
    );

    // Stable component identity — mui-datatables remounts the toolbar subtree
    // whenever this reference changes, so it must not be recreated per render.
    const effectiveToolbar =
        !options?.toolbar ||
        options?.toolbar === "custom" ||
        options?.toolbar === "controls"
            ? RecordTableToolbar
            : options.toolbar === "default"
            ? undefined
            : null;

    // Stable event handlers. These MUST NOT be recreated per render: they feed
    // `tableOptions` below, and mui-datatables' `componentDidUpdate` triggers a
    // full O(rows×columns) table rebuild whenever the `options` prop *reference*
    // changes. Keeping the handlers (and therefore `tableOptions`) referentially
    // stable across re-renders that don't change their inputs is what prevents a
    // live-subscription re-render (or any parent re-render) from rebuilding all
    // rows — the page-size-100 "fans keep whirring" cost.
    // (operator-task-fe-advanced-search-co-list-costly-search-perf)
    const handleChangePage = useCallback(
        (currentPage: number) => {
            if (updateQuery) updateQuery({ offset: currentPage * rowsPerPage });
            if (clientPagePersist) {
                setClientTablePage(currentPage);
                writeSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey, currentPage);
            }
            setSelectedIndexes([]);
            setSelectedItems([]);
        },
        [updateQuery, rowsPerPage, clientPagePersist, clientPageStorageKey, clientPageSegmentKey]
    );

    const handleChangeRowsPerPage = useCallback(
        (numberOfRows: number) => {
            setRowsPerPage(numberOfRows);
            if (updateQuery) updateQuery({ limit: numberOfRows });
            updateLocalStorageTableSettings(tableSettingsKey, { limit: numberOfRows });
            if (clientPagePersist) {
                setClientTablePage(0);
                writeSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey, 0);
            }
        },
        [updateQuery, tableSettingsKey, clientPagePersist, clientPageStorageKey, clientPageSegmentKey]
    );

    const handleColumnSortChange = useCallback(
        (changedColumn: string, direction: string) => {
            if (!updateQuery && !tableSettingsKey) return;

            const initialOrder = direction === "none" && initialQuery?.order;

            const newSortOrder =
                changedColumn === initialQuery?.order.slice(1) && initialOrder && "asc";
            const newQueryOrder = newSortOrder && `+${changedColumn}`;

            setSortOrder({
                name: initialOrder ? getSortOrder(initialOrder).name : changedColumn,
                direction: initialOrder
                    ? newSortOrder || getSortOrder(initialOrder).direction
                    : (direction as "asc" | "desc"),
            });
            const newOrder = initialOrder
                ? newQueryOrder || initialOrder
                : `${direction === "asc" ? "+" : "-"}${changedColumn}`;

            if (updateQuery) {
                updateQuery({
                    order: newOrder,
                });
            }
            if (tableSettingsKey) {
                updateLocalStorageTableSettings(tableSettingsKey, { order: newOrder });
            }
        },
        [updateQuery, tableSettingsKey, initialQuery]
    );

    const handleRowSelectionChange = useCallback(
        (currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: any[]) => {
            const selectedIndexes = allRowsSelected.map((x) => x.dataIndex);
            setSelectedIndexes(selectedIndexes);
            const selectedIndexesSet = new Set(selectedIndexes);
            setSelectedItems(
                displayData.filter((elem: any, index: number) => selectedIndexesSet.has(index))
            );
        },
        [displayData]
    );

    // Grouping: header rows are never selectable and carry a marker class; the
    // caller's own setRowProps / isRowSelectable still apply to data rows.
    const callerSetRowProps = options?.setRowProps;
    const groupedSetRowProps = useCallback(
        (row: any[], dataIndex: number, rowIndex: number) => {
            if (isGroupHeaderRow(displayData[dataIndex])) {
                return { className: "recordTableGroupHeaderRow" };
            }
            return callerSetRowProps ? callerSetRowProps(row, dataIndex, rowIndex) : {};
        },
        [displayData, callerSetRowProps]
    );
    const callerIsRowSelectable = options?.isRowSelectable;
    const groupedIsRowSelectable = useCallback(
        (dataIndex: number) =>
            !isGroupHeaderRow(displayData[dataIndex]) &&
            (callerIsRowSelectable ? callerIsRowSelectable(dataIndex) : true),
        [displayData, callerIsRowSelectable]
    );

    const tableOptions: MUIDataTableOptions = useMemo(
        () => ({
            selectableRows: options?.selectableRows ?? "multiple",
            rowsSelected: selectedIndexes,
            selectToolbarPlacement: "none",
            // Tree mode renders the whole flattened forest client-side (the caller
            // fetches a bounded batch), so it is neither server-paginated nor
            // footer-paginated, and its client-side search/filter are off (they
            // would break parent/child contiguity).
            serverSide: updateQuery && !treeCfg ? true : false,
            count: treeCfg ? displayData.length : totalItemCount,
            rowsPerPage: rowsPerPage,
            rowsPerPageOptions: options?.rowsPerPageOptions ?? [5, 10, 30, 100],
            setRowProps: groupCfg ? groupedSetRowProps : options?.setRowProps,
            onChangePage: handleChangePage,
            onChangeRowsPerPage: handleChangeRowsPerPage,
            onColumnSortChange: handleColumnSortChange,
            onRowSelectionChange: handleRowSelectionChange,
            filter: treeCfg ? false : options?.toolbarFilters ?? true,
            // In tree mode the server-ordered rows are assembled into a forest and
            // must render in that (sibling) order; passing a sortOrder could make
            // client-side mode re-sort the flattened rows and break parent/child
            // contiguity, so drop it.
            sortOrder: treeCfg ? undefined : sortOrder || undefined,
            onSearchChange: onSearchChange,
            searchPlaceholder: searchPlaceholder,
            jumpToPage: options?.jumpToPage ?? true,
            search: treeCfg ? false : options?.showSearch ?? true,
            searchAlwaysOpen: options?.isSearchAlwaysOpen ?? false,
            page: clientPagePersist
                ? clientTablePageForMui
                : options?.currentOffset === 0
                ? 0
                : undefined,
            viewColumns: options?.viewColumns ?? true,
            customFooter: options?.customFooter,
            tableBodyMaxHeight: options?.tableBodyMaxHeight, // Enable scrolling for table body only
            tableBodyHeight: options?.tableBodyHeight, // Fix body to an exact height (stable footer across pages)
            pagination: treeCfg ? false : options?.pagination ?? true,
            isRowSelectable: groupCfg ? groupedIsRowSelectable : options?.isRowSelectable,
            expandableRows: options?.expandableRows ?? false,
            renderExpandableRow: options?.renderExpandableRow,
            rowsExpanded: options?.rowsExpanded,
            onRowExpansionChange: options?.onRowExpansionChange,
            expandableRowsHeader: options?.expandableRowsHeader ?? false,
            expandableRowsOnClick: options?.expandableRowsOnClick ?? false,
            isRowExpandable: options?.isRowExpandable,
        }),
        [
            options?.selectableRows,
            selectedIndexes,
            updateQuery,
            treeCfg,
            displayData,
            totalItemCount,
            rowsPerPage,
            options?.rowsPerPageOptions,
            options?.setRowProps,
            handleChangePage,
            handleChangeRowsPerPage,
            handleColumnSortChange,
            handleRowSelectionChange,
            options?.toolbarFilters,
            sortOrder,
            onSearchChange,
            searchPlaceholder,
            options?.jumpToPage,
            options?.showSearch,
            options?.isSearchAlwaysOpen,
            clientPagePersist,
            clientTablePageForMui,
            options?.currentOffset,
            options?.viewColumns,
            options?.customFooter,
            options?.tableBodyMaxHeight,
            options?.tableBodyHeight,
            options?.pagination,
            options?.isRowSelectable,
            groupCfg,
            groupedSetRowProps,
            groupedIsRowSelectable,
            options?.expandableRows,
            options?.renderExpandableRow,
            options?.rowsExpanded,
            options?.onRowExpansionChange,
            options?.expandableRowsHeader,
            options?.expandableRowsOnClick,
            options?.isRowExpandable,
        ]
    );

    // Stable fallback so a missing `refresh` doesn't produce a new function
    // identity — and therefore a full column rebuild — on every render.
    const noopRefresh = useMemo(() => () => {}, []);
    const muiColumns = useMemo(
        () =>
            cols.map((col: TableColumn) =>
                convertLegacyColumnToMuiColumn(
                    col,
                    displayData,
                    refresh || noopRefresh,
                    {
                        width: columnWidths[col.key],
                        resizable: resizableColumns,
                        onResize: applyColumnWidth,
                        onResetWidth: resetColumnWidth,
                    },
                    treeRenderConfig
                )
            ),
        [
            cols,
            displayData,
            refresh,
            noopRefresh,
            columnWidths,
            resizableColumns,
            applyColumnWidth,
            resetColumnWidth,
            treeRenderConfig,
        ]
    );

    return (
        <div
            ref={recordTableWrapperRef}
            className={`recordTable density-${density}${
                options?.windowedBody ? " windowedBody" : ""
            } ${className ?? ""}`}
        >
            <RecordTableToolbarContext.Provider value={toolbarContextValue}>
                <Table
                    // Remount the inner mui-datatables when the tree/flat mode
                    // flips. mui-datatables (v4) retains the last-applied sort in
                    // its OWN state and keeps using it whenever `options.sortOrder`
                    // has no `.name` (see MUIDataTable: `options.sortOrder.name ?
                    // options.sortOrder : state.sortOrder`) — and `options.sort:
                    // false` does not gate that. So without a remount, entering
                    // tree mode (where we pass `sortOrder: undefined`) keeps
                    // re-sorting the flattened forest by the flat view's last sort
                    // (e.g. created_at desc), which scatters children away from
                    // their parent (a child, being newer, floats ABOVE its
                    // parent). A fresh mount starts with an empty `state.sortOrder`
                    // so the pre-order forest renders in order.
                    // (operator-task-fe-recordtable-tree-mode-phase1-children-tab)
                    key={treeCfg ? "recordtable-tree" : "recordtable-flat"}
                    columns={muiColumns}
                    data={displayData}
                    options={tableOptions}
                    isLoading={isLoading}
                    components={{
                        TableToolbar: effectiveToolbar,
                        // Opt-in spacer-row virtualization (logs listing only).
                        // Undefined -> mui-datatables uses its default body.
                        TableBody: options?.windowedBody ? WindowedTableBody : undefined,
                    }}
                    canSearchEverywhere={options?.canSearchEverywhere}
                    isDisabled={isDisabled}
                />
            </RecordTableToolbarContext.Provider>
            {options?.stickyHorizontalScrollbar && (
                <StickyHorizontalScrollbar tableWrapperRef={recordTableWrapperRef} />
            )}
        </div>
    );
}

/**
 * RecordTable backs every resource/search/dashboard list. It reads nothing from
 * Redux directly — all dynamic data arrives via props — so a live-subscription
 * push re-renders it only because a connected ancestor re-renders. With the
 * options/handlers/columns already memoized (so mui-datatables no longer rebuilds
 * the table), the residual cost is mui-datatables re-rendering ALL cells of the
 * mounted page on each such parent re-render (react-timeago id churn + cell
 * re-stamping) — the page-size-100 "fans keep whirring after load" cost.
 *
 * Wrapping in React.memo (default shallow prop comparison) short-circuits that:
 * when every prop reference is unchanged (fetch-once views like advanced search
 * during a live push), the whole subtree is skipped. It is safe by construction
 * for live-fed callers too — a genuine data change always arrives as a new `data`
 * reference, so the memo re-renders and never freezes live updates; an unstable-
 * prop caller merely gets a harmless no-op. The cast preserves the generic call
 * signature that React.memo would otherwise widen.
 * (operator-task-fe-advanced-search-co-list-costly-search-perf)
 */
export default memo(RecordTable) as typeof RecordTable;
